import React, { FC, useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import { History } from 'history';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Icon from '@material-ui/core/Icon';
import PublicIcon from '@material-ui/icons/PublicOutlined';
import Skeleton from '@material-ui/lab/Skeleton';
import Check from '@material-ui/icons/Check';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import { productsPrefix } from 'routes/routes';
import { PageIndex } from 'components/UI/PageIndex';
import { Row } from 'components/UI/Row';
import { truncateText } from 'helpers/textHelper';
import { ProductActions } from './ProductActions';
import { NoProducts } from './NoProducts';
import { ProductListActions } from './ProductListActions';
import {
  closeError,
  productsApiErrors,
  fetchProducts,
  getProductsList,
  getSearchProductsList,
  isSearch,
  getSearchTerm,
  getLoaders,
  getProductsPagination,
  getGroupComponents,
  resetProductList
} from './products';
import { Product } from './productsTypes';

const ProductList: FC<{}> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history: History = useHistory();
  const tableRef = useRef<HTMLTableElement>(null);
  const productList = useSelector(getProductsList);
  const pagination = useSelector(getProductsPagination);
  const searchList = useSelector(getSearchProductsList);
  const searchTerm = useSelector(getSearchTerm);
  const groupComponents = useSelector(getGroupComponents);
  const isSearching = useSelector(isSearch);
  const { isLoading } = useSelector(getLoaders);
  const { loadingError, deletingError } = useSelector(productsApiErrors);

  const renderList = isSearching ? searchList : productList;
  useEffect(() => {
    dispatch(
      fetchProducts({
        index: 1
      })
    );
    return () => {
      dispatch(resetProductList());
    };
  }, []);

  if (loadingError) {
    return <Alert severity="error">{loadingError}</Alert>;
  }

  return (
    <>
      <ProductListActions />
      <div style={{ height: '20px' }} />
      <NoProducts show={isSearching && renderList.length === 0} />
      <TableContainer
        component={Paper}
        elevation={3}
        style={{ margin: '10px 0 25px', maxHeight: '60vh', height: '100%' }}
      >
        <Table stickyHeader aria-label="order list" ref={tableRef}>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '5%' }}>
                <Icon color="primary">
                  <PublicIcon />
                </Icon>
              </TableCell>
              <TableCell style={{ width: '15%' }}>{t('products.item code')}</TableCell>
              <TableCell>{t('products.name')}</TableCell>
              <TableCell>{t('label.description')}</TableCell>
              <TableCell style={{ width: '10%' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <>
                {Array.from([1, 2, 3, 4, 5], (idx: number) => (
                  <TableRow key={`loading_${idx}`}>
                    <TableCell colSpan={4}>
                      <Skeleton animation="wave" variant="text" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!isLoading && isEmpty(renderList) && (
              <TableRow>
                <TableCell>
                  <Row height="50vh" justifyContent="center" alignItems="center">
                    {t('products.no product found')}
                  </Row>
                </TableCell>
              </TableRow>
            )}
            {renderList.map((p: Product) => (
              <TableRow
                key={`product_${p.id}`}
                hover
                style={{ cursor: 'pointer' }}
                onClick={e => {
                  e.stopPropagation();
                  history.push(`${productsPrefix}/${p.id}/details`);
                }}
              >
                <TableCell style={{ width: '5%' }}>
                  {p.is_public && (
                    <Icon color="primary">
                      <Check />
                    </Icon>
                  )}
                </TableCell>
                <TableCell style={{ width: '15%' }}>{p.item_code}</TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell>{truncateText(p.description, 90)}</TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  <ProductActions product={p} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PageIndex
        justifyContent="center"
        index={pagination.index}
        indexesCount={pagination.indexesCount || 0}
        onChange={async (_, page: number) => {
          await dispatch(fetchProducts({ index: page, search: searchTerm, groupcomponents: groupComponents }));
          if (tableRef) {
            tableRef?.current?.scrollIntoView();
            window.scrollTo({ top: 0 });
          }
        }}
      />
      {/* TODO: REFACTOR SNACKBAR IN SINGLE SHARED COMPONENT */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key="bottom,center"
        open={!isEmpty(deletingError)}
        autoHideDuration={5000}
        onClose={() => {
          dispatch(closeError());
        }}
      >
        <Alert severity="error" variant="filled" onClose={() => dispatch(closeError())}>
          {deletingError}
        </Alert>
      </Snackbar>
    </>
  );
};

export { ProductList };
